import React, { useRef, useEffect } from 'react';
import { Calendar, Clock, Check, X, HelpCircle, Info } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { MAX_MINIMUM_BOOKING_NOTICE_MINUTES } from '../utils/schedulingConfig.ts';

interface DayConfig {
  open: boolean;
  morningEnabled: boolean;
  morningStart: string;
  morningEnd: string;
  afternoonEnabled: boolean;
  afternoonStart: string;
  afternoonEnd: string;
}

const defaultDay: DayConfig = {
  open: false,
  morningEnabled: false,
  morningStart: '',
  morningEnd: '',
  afternoonEnabled: false,
  afternoonStart: '',
  afternoonEnd: ''
};

// Função para converter dados antigos para o novo formato
const convertOldConfig = (oldConfig: any): DayConfig => {
  // Se já estiver no novo formato, retorna como está
  if ('morningEnabled' in oldConfig && 'afternoonEnabled' in oldConfig) {
    return oldConfig as DayConfig;
  }

  // Caso contrário, converte do formato antigo
  return {
    open: false, // Começamos com false e vamos habilitar baseado nos períodos
    morningEnabled: false,
    morningStart: '',
    morningEnd: '',
    afternoonEnabled: false,
    afternoonStart: '',
    afternoonEnd: ''
  };
};

interface SchedulingConfigFormProps {
  persistOnChange?: boolean;
  monday: DayConfig; setMonday: (val: DayConfig) => void;
  tuesday: DayConfig; setTuesday: (val: DayConfig) => void;
  wednesday: DayConfig; setWednesday: (val: DayConfig) => void;
  thursday: DayConfig; setThursday: (val: DayConfig) => void;
  friday: DayConfig; setFriday: (val: DayConfig) => void;
  saturday: DayConfig; setSaturday: (val: DayConfig) => void;
  sunday: DayConfig; setSunday: (val: DayConfig) => void;

  consultationDuration: number;
  setConsultationDuration: (val: number) => void;
  numberOfSuggestions: number;
  setNumberOfSuggestions: (val: number) => void;
  minimumBookingNoticeMinutes?: number;
  setMinimumBookingNoticeMinutes?: (val: number) => void;
  disabledSuggestions?: boolean;
}

interface DayRowProps {
  persistOnChange?: boolean;
  label: string;
  dayConfig: DayConfig;
  setDayConfig: (val: DayConfig) => void;
}

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className="relative inline-flex group" tabIndex={0} aria-label={text}>
    <Info className="h-3.5 w-3.5 text-gray-400 transition-colors group-hover:text-sminy-primary" />
    <span
      role="tooltip"
      className="pointer-events-none absolute left-1/2 top-5 z-20 w-64 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs font-normal leading-5 text-gray-700 opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus:opacity-100"
    >
      {text}
    </span>
  </span>
);

// Removendo a interface TimeInputRefs pois usaremos inferência de tipo

const DayRow: React.FC<DayRowProps> = ({ label, dayConfig, setDayConfig, persistOnChange = false }) => {
  const { isDark } = useTheme();

  // Criar refs para os inputs de tempo
  const morningStartRef = useRef<HTMLInputElement>(null);
  const morningEndRef = useRef<HTMLInputElement>(null);
  const afternoonStartRef = useRef<HTMLInputElement>(null);
  const afternoonEndRef = useRef<HTMLInputElement>(null);

  // Usando refs diretamente para evitar problemas de tipagem

  // Sincronizar refs com props quando as props mudarem
  useEffect(() => {
    if (morningStartRef.current) {
      morningStartRef.current.value = dayConfig.morningStart;
    }
    if (morningEndRef.current) {
      morningEndRef.current.value = dayConfig.morningEnd;
    }
    if (afternoonStartRef.current) {
      afternoonStartRef.current.value = dayConfig.afternoonStart;
    }
    if (afternoonEndRef.current) {
      afternoonEndRef.current.value = dayConfig.afternoonEnd;
    }
  }, [dayConfig]);

  const handlePeriodToggle = (period: 'morning' | 'afternoon') => {
    // Salvar posição atual do scroll
    const currentScrollY = window.scrollY;

    if (period === 'morning') {
      const newState = !dayConfig.morningEnabled;
      const newConfig = {
        ...dayConfig,
        morningEnabled: newState,
        morningStart: newState ? '08:00' : '',
        morningEnd: newState ? '12:00' : '',
      };
      newConfig.open = newConfig.morningEnabled || newConfig.afternoonEnabled;
      setDayConfig(newConfig);
    } else {
      const newState = !dayConfig.afternoonEnabled;
      const newConfig = {
        ...dayConfig,
        afternoonEnabled: newState,
        afternoonStart: newState ? '14:00' : '',
        afternoonEnd: newState ? '18:00' : '',
      };
      newConfig.open = newConfig.morningEnabled || newConfig.afternoonEnabled;
      setDayConfig(newConfig);
    }

    // Manter posição de scroll após a atualização
    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  };

  const handleTimeBlur = (period: 'morning' | 'afternoon', timeType: 'start' | 'end') => {
    const newConfig = { ...dayConfig };

    if (period === 'morning') {
      if (timeType === 'start' && morningStartRef.current) {
        newConfig.morningStart = morningStartRef.current.value;
      } else if (timeType === 'end' && morningEndRef.current) {
        newConfig.morningEnd = morningEndRef.current.value;
      }
    } else {
      if (timeType === 'start' && afternoonStartRef.current) {
        newConfig.afternoonStart = afternoonStartRef.current.value;
      } else if (timeType === 'end' && afternoonEndRef.current) {
        newConfig.afternoonEnd = afternoonEndRef.current.value;
      }
    }

    newConfig.open = newConfig.morningEnabled || newConfig.afternoonEnabled;
    setDayConfig(newConfig);
  };

  return (
    <div className={`p-4 rounded-xl border mb-3 ${isDark
      ? 'bg-gray-800/30 border-gray-600'
      : 'bg-gray-50/30 border-gray-200'
      }`}>
      <div className="flex items-center justify-between mb-4">
        <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'
          }`}>{label}</span>
        <div className={`w-2 h-2 rounded-full ${(dayConfig.morningEnabled || dayConfig.afternoonEnabled) ? 'bg-sminy-primary' : 'bg-gray-400'
          }`}></div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Manhã */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={dayConfig.morningEnabled}
              onChange={() => handlePeriodToggle('morning')}
              className="h-4 w-4 rounded border-gray-300 text-sminy-primary focus:ring-sminy-primary"
            />
            <span className={`flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Atende de manhã
              <InfoTooltip text={`Marque se a clínica atende pacientes pela manhã em ${label}.`} />
            </span>
          </label>

          {dayConfig.morningEnabled && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`mb-1 flex items-center gap-1.5 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  Início <span className="text-red-500">*</span>
                  <InfoTooltip text={`Horário em que começa o atendimento da manhã em ${label}.`} />
                </label>
                <input
                  ref={morningStartRef}
                  type="time"
                  required
                  className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${isDark
                    ? 'border-gray-600 bg-gray-700 text-gray-200 focus:border-sminy-primary'
                    : 'border-gray-300 bg-white text-gray-800 focus:border-sminy-primary'
                    }`}
                  defaultValue={dayConfig.morningStart}
                  aria-label={`${label} · Manhã · Início`}
                  onChange={persistOnChange ? () => handleTimeBlur('morning', 'start') : undefined}
                  onBlur={() => handleTimeBlur('morning', 'start')}
                />
              </div>
              <div>
                <label className={`mb-1 flex items-center gap-1.5 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  Fim <span className="text-red-500">*</span>
                  <InfoTooltip text={`Horário em que termina o atendimento da manhã em ${label}.`} />
                </label>
                <input
                  ref={morningEndRef}
                  type="time"
                  required
                  className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${isDark
                    ? 'border-gray-600 bg-gray-700 text-gray-200 focus:border-sminy-primary'
                    : 'border-gray-300 bg-white text-gray-800 focus:border-sminy-primary'
                    }`}
                  defaultValue={dayConfig.morningEnd}
                  aria-label={`${label} · Manhã · Fim`}
                  onChange={persistOnChange ? () => handleTimeBlur('morning', 'end') : undefined}
                  onBlur={() => handleTimeBlur('morning', 'end')}
                />
              </div>
            </div>
          )}
        </div>

        {/* Tarde */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={dayConfig.afternoonEnabled}
              onChange={() => handlePeriodToggle('afternoon')}
              className="h-4 w-4 rounded border-gray-300 text-sminy-primary focus:ring-sminy-primary"
            />
            <span className={`flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Atende à tarde
              <InfoTooltip text={`Marque se a clínica atende pacientes à tarde em ${label}.`} />
            </span>
          </label>

          {dayConfig.afternoonEnabled && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={`mb-1 flex items-center gap-1.5 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  Início <span className="text-red-500">*</span>
                  <InfoTooltip text={`Horário em que começa o atendimento da tarde em ${label}.`} />
                </label>
                <input
                  ref={afternoonStartRef}
                  type="time"
                  required
                  className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${isDark
                    ? 'border-gray-600 bg-gray-700 text-gray-200 focus:border-sminy-primary'
                    : 'border-gray-300 bg-white text-gray-800 focus:border-sminy-primary'
                    }`}
                  defaultValue={dayConfig.afternoonStart}
                  aria-label={`${label} · Tarde · Início`}
                  onChange={persistOnChange ? () => handleTimeBlur('afternoon', 'start') : undefined}
                  onBlur={() => handleTimeBlur('afternoon', 'start')}
                />
              </div>
              <div>
                <label className={`mb-1 flex items-center gap-1.5 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                  Fim <span className="text-red-500">*</span>
                  <InfoTooltip text={`Horário em que termina o atendimento da tarde em ${label}.`} />
                </label>
                <input
                  ref={afternoonEndRef}
                  type="time"
                  required
                  className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${isDark
                    ? 'border-gray-600 bg-gray-700 text-gray-200 focus:border-sminy-primary'
                    : 'border-gray-300 bg-white text-gray-800 focus:border-sminy-primary'
                    }`}
                  defaultValue={dayConfig.afternoonEnd}
                  aria-label={`${label} · Tarde · Fim`}
                  onChange={persistOnChange ? () => handleTimeBlur('afternoon', 'end') : undefined}
                  onBlur={() => handleTimeBlur('afternoon', 'end')}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper components (seguindo padrão)
const Field: React.FC<{ label: string; children: React.ReactNode; hint?: string; tooltip: string; required?: boolean }> = ({
  label,
  children,
  hint,
  tooltip,
  required = true
}) => {
  const { isDark } = useTheme();
  return (
    <label className="block text-sm">
      <span className={`mb-1 flex items-center gap-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'
        }`}>
        <span>{label}{required && <span className="text-red-500"> *</span>}</span>
        <InfoTooltip text={tooltip} />
      </span>
      {children}
      {hint && <span className={`mt-1 block text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'
        }`}>{hint}</span>}
    </label>
  );
};

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  const { isDark } = useTheme();
  return (
    <input
      ref={ref}
      {...props}
      className={`w-full rounded-xl border px-3 py-2 text-sm outline-none transition-all focus:ring-2 focus:ring-sminy-primary ${isDark
        ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-400 focus:border-sminy-primary'
        : 'border-gray-300 bg-white text-gray-800 placeholder:text-gray-400 focus:border-sminy-primary'
        } ${props.className ?? ""}`}
    />
  );
});


const SchedulingConfigForm: React.FC<SchedulingConfigFormProps> = ({
  persistOnChange = false,
  monday, setMonday,
  tuesday, setTuesday,
  wednesday, setWednesday,
  thursday, setThursday,
  friday, setFriday,
  saturday, setSaturday,
  sunday, setSunday,
  consultationDuration, setConsultationDuration,
  numberOfSuggestions, setNumberOfSuggestions,
  minimumBookingNoticeMinutes,
  setMinimumBookingNoticeMinutes,
  disabledSuggestions = false
}) => {
  const { isDark } = useTheme();

  // Refs para campos de configuração
  const consultationDurationRef = useRef<HTMLInputElement>(null);
  const numberOfSuggestionsRef = useRef<HTMLInputElement>(null);
  const minimumBookingNoticeMinutesRef = useRef<HTMLInputElement>(null);

  // Sincronizar refs com props quando as props mudarem
  useEffect(() => {
    if (consultationDurationRef.current) {
      consultationDurationRef.current.value = consultationDuration.toString();
    }
    if (numberOfSuggestionsRef.current) {
      numberOfSuggestionsRef.current.value = numberOfSuggestions.toString();
    }
    if (
      minimumBookingNoticeMinutesRef.current
      && minimumBookingNoticeMinutes !== undefined
    ) {
      minimumBookingNoticeMinutesRef.current.value = minimumBookingNoticeMinutes.toString();
    }
  }, [consultationDuration, minimumBookingNoticeMinutes, numberOfSuggestions]);

  // Converter as configurações existentes para o novo formato ao montar o componente
  useEffect(() => {
    setMonday(convertOldConfig(monday));
    setTuesday(convertOldConfig(tuesday));
    setWednesday(convertOldConfig(wednesday));
    setThursday(convertOldConfig(thursday));
    setFriday(convertOldConfig(friday));
    setSaturday(convertOldConfig(saturday));
    setSunday(convertOldConfig(sunday));
  }, []); // Executar apenas na montagem do componente

  // Manipuladores para atualizar estados quando o input perde o foco
  const handleConsultationDurationBlur = () => {
    if (consultationDurationRef.current) {
      const value = Number(consultationDurationRef.current.value);
      if (!isNaN(value) && value > 0) {
        setConsultationDuration(value);
      }
    }
  };

  const handleNumberOfSuggestionsBlur = () => {
    if (numberOfSuggestionsRef.current) {
      const value = Number(numberOfSuggestionsRef.current.value);
      if (!isNaN(value) && value > 0 && value <= 5) {
        setNumberOfSuggestions(value);
      }
    }
  };

  const handleMinimumBookingNoticeMinutesBlur = () => {
    if (
      !minimumBookingNoticeMinutesRef.current
      || minimumBookingNoticeMinutes === undefined
      || !setMinimumBookingNoticeMinutes
    ) {
      return;
    }

    const rawValue = minimumBookingNoticeMinutesRef.current.value.trim();
    const value = Number(rawValue);
    if (
      rawValue !== ''
      && Number.isSafeInteger(value)
      && value >= 0
      && value <= MAX_MINIMUM_BOOKING_NOTICE_MINUTES
    ) {
      setMinimumBookingNoticeMinutes(value);
      return;
    }

    minimumBookingNoticeMinutesRef.current.value = minimumBookingNoticeMinutes.toString();
  };

  return (
    <div className="space-y-4">
      {persistOnChange && <h2 className="register-section-title">Dias e horários</h2>}
      {/* Dias da semana */}
      <div className="space-y-3">
        <DayRow persistOnChange={persistOnChange} label="Segunda" dayConfig={monday} setDayConfig={setMonday} />
        <DayRow persistOnChange={persistOnChange} label="Terça" dayConfig={tuesday} setDayConfig={setTuesday} />
        <DayRow persistOnChange={persistOnChange} label="Quarta" dayConfig={wednesday} setDayConfig={setWednesday} />
        <DayRow persistOnChange={persistOnChange} label="Quinta" dayConfig={thursday} setDayConfig={setThursday} />
        <DayRow persistOnChange={persistOnChange} label="Sexta" dayConfig={friday} setDayConfig={setFriday} />
        <DayRow persistOnChange={persistOnChange} label="Sábado" dayConfig={saturday} setDayConfig={setSaturday} />
        <DayRow persistOnChange={persistOnChange} label="Domingo" dayConfig={sunday} setDayConfig={setSunday} />
      </div>

      {/* Separação visual clara */}
      <div className={`border-t pt-4 ${isDark ? 'border-gray-600' : 'border-gray-200'
        }`}>
        <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>{persistOnChange ? 'Regras da avaliação' : 'Configurações Gerais'}</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Duração das consultas" hint="Tempo médio em minutos" tooltip="Tempo reservado para cada avaliação na agenda.">
            <Input
              ref={consultationDurationRef}
              type="number"
              required
              defaultValue={consultationDuration}
              onChange={persistOnChange ? handleConsultationDurationBlur : undefined}
              onBlur={handleConsultationDurationBlur}
              min="1"
              placeholder="30"
            />
          </Field>

          {minimumBookingNoticeMinutes !== undefined && setMinimumBookingNoticeMinutes && (
            <Field
              label="Antecedência mínima para o agente oferecer horários"
              hint="Em minutos, de 0 a 43.200. Use 0 para permitir qualquer horário futuro."
              tooltip="Horários que começarem antes desse intervalo não serão oferecidos pelo agente, inclusive em agendas Google e Clinicorp."
            >
              <Input
                ref={minimumBookingNoticeMinutesRef}
                type="number"
                required
                defaultValue={minimumBookingNoticeMinutes}
                onChange={persistOnChange ? handleMinimumBookingNoticeMinutesBlur : undefined}
                onBlur={handleMinimumBookingNoticeMinutesBlur}
                min="0"
                max={MAX_MINIMUM_BOOKING_NOTICE_MINUTES.toString()}
                step="1"
                inputMode="numeric"
                placeholder="180"
              />
            </Field>
          )}

          <Field label="Sugestões de horário" hint="Quantas opções oferecer (1-5)" tooltip="Quantidade de opções que o assistente oferece ao paciente. Está travado em 2 para manter o padrão do fluxo.">
            <Input
              ref={numberOfSuggestionsRef}
              type="number"
              required
              defaultValue={numberOfSuggestions}
              onChange={persistOnChange ? handleNumberOfSuggestionsBlur : undefined}
              onBlur={handleNumberOfSuggestionsBlur}
              disabled={disabledSuggestions}
              min="1"
              max="5"
              placeholder="3"
              className={disabledSuggestions ? '!bg-gray-200 !text-gray-500 cursor-not-allowed border-gray-300' : ''}
            />
          </Field>
        </div>
      </div>
    </div>
  );
};

export default SchedulingConfigForm;
